/**
 * @file 提供给 table item 的 formatter
 */
import numeral from 'numeral';
import store from 'src/store';
import { isAdministrator } from 'src/utils/authUtil';
import { quailficationRejectReasonType } from 'src/const/state/account';
import SkanNumberHover from 'src/components/dashboard/perf/skan-num-hover';
import { isPcmPhase2White } from 'src/const/whiteList';
import tool from 'src/utils/tool';
import { LeadBCShare } from 'src/const/state/creative';
import exportLeadAds from 'src/utils/exportLeadAds';
import {
  getUserCurrency,
  formatBrandMoney,
  percentileDivisionFormatMoney,
} from 'src/mixins/currencyMixin';
import { campaignObjectiveValue, campaignType } from 'src/const/state/campaign';

import { currencyLocaleFormat } from 'src/utils/currencyUtil';

export const isPcmWebStatistic = (row) => {
  const {
    objective_type, ad_ref_pixel_id, promotion_website_type, dedicate_type,
  } = row;
  return isPcmPhase2White() && ad_ref_pixel_id && !(+promotion_website_type) && dedicate_type !== campaignType.dedicatedCampaign &&
    [campaignObjectiveValue.Traffic, campaignObjectiveValue.DPA,
      campaignObjectiveValue.Conversion, campaignObjectiveValue.Shop].includes(objective_type);
};

export const downLoadAdsForm = (requestParams, $t, statusChangeFn = () => { }) => {
  // 只有管理员能下载广告主数据
  if (!isAdministrator()) {
    return;
  }
  statusChangeFn(true);
  exportLeadAds({
    requestParams,
    teaParams: {
      download_source: 1,
      adv_id: store.state.account.permissionData?.account?.id,
      ...requestParams,
    },
    finallyCb: () => statusChangeFn(false),
  });
};
// 统计的数据list和view data, 素材库层级不一样，需要考虑两种情况的处理
export const getStatisticDataKey = (row, key) => {
  const itemKey = (typeof (row.stat_data && row.stat_data[key]) !== 'undefined') ?
    (row.stat_data && row.stat_data[key]) : (row[key]);
  return itemKey;
};
export const currencyKey = getUserCurrency();

// 数据转化成整数型
export const integerValueFormatter = (row, col, cellValue) => {
  if (typeof cellValue === 'undefined' || cellValue === '-') {
    return '-';
  }
  if (window.CURRENCY_LOCALE) {
    return currencyLocaleFormat(cellValue, 0);
  }
  return numeral(cellValue).format('0,0');
};

// 如果没数据显示 '-'
export const noneFormatter = (row, col, cellValue) => {
  if (!cellValue) {
    return '-';
  }
  if (window.CURRENCY_LOCALE) {
    return currencyLocaleFormat(cellValue);
  }
  return cellValue;
};

// 百分比 formatter，用于表头
export const percentTextFormatter = str => `${str} (%)`;

// 百分比 formatter，用于表格内部数据
export const percentCellFormatter = (row, col, cellValue) => {
  if (typeof cellValue === 'undefined' || cellValue === '-') {
    return '-';
  }
  if (!cellValue) {
    return '0%';
  }
  if (window.CURRENCY_LOCALE) {
    return `${currencyLocaleFormat(cellValue)}%`;
  }
  return `${cellValue}%`;
};

// 小数转化为百分数
export const decimalTransferpercentFormatter = (num = 0) => {
  const _multiply = (num1, num2) => {
    let m = 0;
    const s1 = num1.toString();
    const s2 = num2.toString();
    try {
      m += s1.split('.')[1].length;
    } catch (e) {
      // error
    }
    try {
      m += s2.split('.')[1].length;
    } catch (e) {
      // error
    }
    return Number(s1.replace('.', '')) * Number(s2.replace('.', '')) / (10 ** m);
  };

  return `${_multiply(num, 100)}%`;
};


// 金钱 formatter（不包含单位）
export const moneyFormatter = (row, col, cellValue) => percentileDivisionFormatMoney(cellValue);

// 金钱 formatter（包含单位）
export const moneyFormatterWithUnit = (row, col, cellValue) => {
  const currencyKey = getUserCurrency();
  if (typeof cellValue === 'undefined' || cellValue === '-') {
    return '-';
  }
  return `${moneyFormatter(row, col, cellValue)} ${currencyKey}`;
};

// 货币单位 formatter 用于图表绘制
export const moneyUnitTextFormatter = (str) => {
  const currencyKey = getUserCurrency();
  return `${str}(${currencyKey})`;
};

// 金钱 formatter，用于 title
export const moneyUnitFormatter = (str) => {
  const currencyKey = getUserCurrency();
  return `${str} (${currencyKey})`;
};

// 时长 formatter
export const durationFormatter = (row, col, cellValue) => {
  if (typeof cellValue === 'undefined' || cellValue === '-') {
    return '-';
  }
  if (window.CURRENCY_LOCALE) {
    return currencyLocaleFormat(cellValue, 2);
  }
  return numeral(cellValue).format('0,0.00');
}; // 播放时长格式化

// 成效数 formatter
// FIX: ReferenceError: h is not defined
// https://github.com/vuejs/jsx/issues/64
// https://github.com/vuejs/babel-plugin-transform-vue-jsx#h-auto-injection
// isTimeAttr 涉及一个转化对回的概念，表现是字段前缀是否是 time_attr_*，具体文档：https://bytedance.feishu.cn/docs/doccnnyOwM83ciI4AOuDKVFDTed
export function effectCountFomatter(row, h, isTimeAttr, $t, instance) {
  const {
    effect_note, dedicate_type, objective_type,
  } = row;
  const isMMPDcData = dedicate_type === campaignType.dedicatedCampaign;
  const isAlreadyShared = row?.page_id_status === LeadBCShare.ISSHARED;
  const isLeadsModalDisabled = !isAdministrator() || isAlreadyShared;
  const leadDownClass = ['display-status', 'lead-ads-status', { 'lead-ads-disable': isLeadsModalDisabled }];
  const isPcmWeb = isPcmWebStatistic(row);
  const hintKey = isPcmWeb ? 'module_dc_hover_pcm' : 'module_dc_hover_idmatching';
  const getDownloadTooltip = () => {
    if (!isAdministrator()) {
      return $t('module_lead_ads_form_download_hint');
    }
    if (isAlreadyShared) {
      return $t('download_leads_tooltip');
    }
    return $t('module_lead_ads_form_download_hint');
  };

  if (+objective_type === campaignObjectiveValue.LeadAds && window.location.pathname === '/i18n/perf/creative') {
    return (
      <vi-tooltip
        content={getDownloadTooltip()}
        effect='light'
      >
        <div
          class={leadDownClass}
          data-tea-expose='Leadads_adlist_impression'
          data-tea-click='Leadads_adlist_click'
          data-tea-results='download_leads_ad'
          onClick={() => !isLeadsModalDisabled && instance.$bus.$emit('toggle-download-leads', row)}>
          {$t('module_lead_ads_form_download')}
        </div>
      </vi-tooltip>
    );
  }

  let count = 0;
  count = isTimeAttr ? getStatisticDataKey(row, 'time_attr_effect_cnt') : getStatisticDataKey(row, 'effect_cnt');
  const effectCount = getStatisticDataKey(row, 'time_attr_effect_cnt') === '-' ? '-' : integerValueFormatter('', '', count);
  const numberData = effectCount;
  const formatNumberData = typeof numberData === 'undefined' ? '-' : numberData;
  if (!isMMPDcData && !isPcmWeb) {
    return (
      <div class="display-status">
        <div class="main-status">
          {formatNumberData}
        </div>
        <div class="bid-status">{effect_note}</div>
      </div>);
  }
  return (
    <SkanNumberHover
      isMMPDcData={isMMPDcData}
      isPcmWeb={isPcmWeb}
      isShowNote={true}
      tooltipTextKey={hintKey}
      numberData={numberData}
      noteData={effect_note}
    />
  );
}

// 成效成本 formatter
export function effectCostFomatter(row, h, isTimeAttr) {
  const { objective_type, effect_cost_note, dedicate_type } = row;
  const isMMPDcData = dedicate_type === campaignType.dedicatedCampaign;
  const currencyKey = getUserCurrency();
  const isPcmWeb = isPcmWebStatistic(row);
  let effectCost = 0;
  effectCost = isTimeAttr ? getStatisticDataKey(row, 'time_attr_effect_cost') : getStatisticDataKey(row, 'effect_cost');
  if (effectCost !== '-') {
    if (objective_type === campaignObjectiveValue.Reach || objective_type === campaignObjectiveValue.VideoView) {
      effectCost = `${formatBrandMoney(effectCost)}${currencyKey}`;
    } else {
      effectCost = `${percentileDivisionFormatMoney(effectCost)}${currencyKey}`;
    }
  }
  const hintKey = isPcmWeb ? 'module_dc_hover_pcm' : 'module_dc_hover_idmatching';
  const numberData = effectCost;
  const formatNumberData = typeof numberData === 'undefined' ? '-' : numberData;
  if (!isMMPDcData && !isPcmWeb) {
    return (
      <div class="display-status">
        <div class="main-status">
          {formatNumberData}
        </div>
        <div class="bid-status">{effect_cost_note}</div>
      </div>);
  }
  return (
    <SkanNumberHover
      isMMPDcData={isMMPDcData}
      isPcmWeb={isPcmWeb}
      isShowNote={true}
      tooltipTextKey={hintKey}
      numberData={numberData}
      noteData={effect_cost_note}
    />
  );
}

// 成效率 formatter
export function effectRateFormatter(row, h, isTimeAttr) {
  const {
    effect_rate_note, dedicate_type,
  } = row;
  const isMMPDcData = dedicate_type === campaignType.dedicatedCampaign;
  const isPcmWeb = isPcmWebStatistic(row);
  const hintKey = isPcmWeb ? 'module_dc_hover_pcm' : 'module_dc_hover_idmatching';

  let effectRate = 0;
  if (row.pricing !== 'CPM') {
    effectRate = isTimeAttr ? getStatisticDataKey(row, 'time_attr_effect_rate') : getStatisticDataKey(row, 'effect_rate');
    effectRate = [undefined, null, '-'].includes(effectRate) ? effectRate : `${effectRate}%`;
  } else {
    effectRate = '-';
  }
  const numberData = effectRate;
  const formatNumberData = typeof numberData === 'undefined' ? '-' : numberData;
  if (!isMMPDcData && !isPcmWeb) {
    return (
      <div class="display-status">
        <div class="main-status">
          {formatNumberData}
        </div>
        <div class="bid-status">{effect_rate_note}</div>
      </div>);
  }
  return (
    <SkanNumberHover
      isMMPDcData={isMMPDcData}
      isPcmWeb={isPcmWeb}
      isShowNote={true}
      tooltipTextKey={hintKey}
      numberData={numberData}
      noteData={effect_rate_note}
    />
  );
}

// 建议 formatter
export function suggestionFormatter(data, h, hasPharmacyQualification, isSelfAccount, aadvid, $t, creativeName = '') {
  if (!data || !data.suggestion || !data.suggestion.length || data.suggestion === '-') {
    return '-';
  }
  // 医药类拒绝的response code
  const medicineRejectCode = [101703, 101706, 101707, 101708, 101709, 101713];
  let jumpUrl = '/help/article?aid=6684149081637388293';
  let jumpText = $t('common_learn_more');

  // 通过白名单枚举方式控制医药资质入口
  const isMedicineQulification = medicineRejectCode.includes(data.disapproval_code) && hasPharmacyQualification;
  if (isMedicineQulification) {
    jumpUrl = `/i18n/account/qualification_list?aadvid=${aadvid}`;
    jumpText = $t('module_account_qualification_supplement');
  }

  // 音乐资质拒审 跳转至添加资质页面
  const isMusicQualification = data.reason_type === quailficationRejectReasonType.MUSIC;
  if (isMusicQualification) {
    const creative_id = data.creative_id || data.cid || '';
    const creative_name = data.name || creativeName;
    const identity_id = data.identity_id || '';
    const identity_type = data.identity_type || '';

    const temp = {
      aadvid,
      source: 'reject_suggestion',
      creative_id,
      creativeName: encodeURIComponent(creative_name),
      identity_type,
      identity_id,
    };
    jumpUrl = `/i18n/account/qualification_list${tool.jsonToQuery(temp)}`;
  }


  const showBot = isSelfAccount && data.disapproval_code && store.state.isAthenaCallerReady;

  // 行业拒审 code 需要拼接禁投国家传给 bot
  const industryDisapprovalCodes = [100125, 100126, 100127, 100128, 100135];

  const forbiddenCountry = industryDisapprovalCodes.includes(data.disapproval_code) && data.forbidden_country?.length === 1
    ? data.forbidden_country[0]
    : '';

  const materialCode = `${data.disapproval_code}${forbiddenCountry ? `_${forbiddenCountry}` : ''}`;
  const revokeBot = () => {
    window.intelliCS && window.intelliCS.openDialog({
      question: {
        type: 'best_practice',
        keyword: materialCode,
      },
    });
    window._app && window._app.$tea.trackEvent('suggestion_learnmore_bot_click');
  };

  return <div class="audit-suggestion">
    <span domPropsInnerHTML={data.suggestion} />
    {showBot
      ? <a onClick={revokeBot}> {$t('common_learn_more')} </a>
      : <vi-learn-more link={jumpUrl}> {jumpText} </vi-learn-more>
    }
  </div>;
}


export { currencyLocaleFormat };
